/**
 * Deity acquisition engine — the ONE counting engine: points (Master Book
 * rulings 1.5–1.8, point table approved 7 Oct 2026). Pure functions over a
 * completion log + the per-day points ledger (gameSettings.ts); nothing here
 * touches the DB or React. Every point is recomputed from those two sources,
 * so banked levels are never lost.
 *
 * Point values:
 *   Step action     +1  — each completed action in the principle completion log
 *   ORDER row green +1  — each Today-orb row green (ordered day = all five = +5)
 *   NUMA greens     +2  — Charge/Load/Flow all green at the day's close
 *   Evening session +1  — the session that closes the day
 *   AIM            +30  — a week holding `game.aimThreshold` ordered days
 *   GOAL          +150  — a "month" (`game.goalWindowWeeks` weeks) holding
 *                         `game.goalThreshold` ordered weeks
 *   Acquire at 400; further levels per LEVEL_THRESHOLDS.
 *
 * There is NO forgiveness (ruling 1.12): no bypass tokens, no challenge
 * recovery, no rescue of short weeks or bridged gaps. Challenge bonus points
 * are removed as well — award mechanics beyond the table above are an Open
 * Decision in the Master Book (Appendix · Open Decisions), so none are invented.
 *
 * The ledger's day awards (rows / NUMA / evening) and the Aim/Goal windows are
 * system-wide; they accrue to a seat once it is engaged (has picks or a log).
 * Step actions come only from the seat's own principle log, so a rep is never
 * counted twice (the ledger's own `stepActions` is not added on top).
 */
import {
  ACQUIRE_POINTS as ACQUIRE_BAR,
  AWARD,
  dayPoints,
  EMPTY_DAY,
  DEFAULT_GAME_SETTINGS,
  isWeekBoundaryEvening,
  type GameSettings,
  type PointsLedger,
} from './gameSettings';
import { goalAwardWeeks, orderedWeeks } from '../lib/stage-ladder';

/** Cumulative points to COMPLETE each level. L1 (400) = basic acquisition; each
 *  higher level costs more and re-populates the pick list with advanced usage
 *  principles. Banked levels never reset — you keep climbing the same total. */
export const LEVEL_THRESHOLDS = [ACQUIRE_BAR, 700, 1000, 1300, 1600] as const;
/** Level 1 threshold — the "acquired" bar (kept as a named export). */
export const ACQUIRE_POINTS = LEVEL_THRESHOLDS[0];
export const PTS = { STEP: AWARD.STEP_ACTION, ORDER_ROW: AWARD.ORDER_ROW_GREEN, NUMA: AWARD.NUMA_THREE_GREENS, EVENING: AWARD.EVENING_SESSION, AIM: AWARD.AIM, GOAL: AWARD.GOAL } as const;

/** Level (1-based) you're currently working toward + its point window. */
export function levelInfo(points: number): { level: number; floor: number; target: number; maxed: boolean } {
  let idx = 0;
  while (idx < LEVEL_THRESHOLDS.length && points >= LEVEL_THRESHOLDS[idx]) idx += 1;
  const maxed = idx >= LEVEL_THRESHOLDS.length;
  const level = maxed ? LEVEL_THRESHOLDS.length : idx + 1;
  const floor = idx > 0 ? LEVEL_THRESHOLDS[idx - 1] : 0;
  const target = LEVEL_THRESHOLDS[Math.min(idx, LEVEL_THRESHOLDS.length - 1)];
  return { level, floor, target, maxed };
}
/** Per-deity persisted progress: active principle picks + the completion log,
 *  both keyed by stable principle id (`toolId:levelIdx:principleIdx`). */
export interface DeityProgress {
  /** Active principle ids. */
  picks: string[];
  /** 'YYYY-MM-DD' → principle ids completed that day. */
  log: Record<string, string[]>;
}

export const EMPTY_PROGRESS: DeityProgress = { picks: [], log: {} };

/** The shared points ledger + the user's game rules (from useCompassConfig). */
export interface ProgressContext {
  ledger?: PointsLedger;
  game?: GameSettings;
}

export interface ProgressStats {
  points: number;
  /** Step actions (principle completions) this season. */
  steps: number;
  /** Ordered weeks this season (+30 each). */
  aims: number;
  /** Ordered months this season (+150 each). */
  goals: number;
  /** Ordered days (all five ORDER rows green) this season. */
  todayCount: number;
  /** Points from ledger day awards (rows, NUMA greens, evening) this season. */
  dayAwards: number;
  /** 0 STEP · 1 TODAY · 2 AIM · 3 GOAL · 4 SECURED — drives the front dots. */
  tierIdx: number;
  /** True once Level 1 (basic acquisition) is reached. */
  secured: boolean;
  /** Current level (1-based) being worked toward. */
  level: number;
  /** Cumulative points that complete the current level. */
  levelTarget: number;
  /** Cumulative points at the start of the current level. */
  levelFloor: number;
  /** True at the top defined level. */
  maxed: boolean;
  /** 0..1 fill within the current level (floor→target). */
  pct: number;
  /** Average points/day since the first award this season. */
  ratePerDay: number;
  /** Human date the deity is acquired at current pace, or null if unknowable. */
  projectedDate: string | null;
  /** When the current season ends and un-banked points refresh. */
  seasonEnd: string | null;
  /** Principle ids already logged today. */
  doneToday: string[];
  /** Active pick ids for the current level (picks ∩ current-level ids). */
  activePicks: string[];
}

const MS_DAY = 24 * 3600 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse 'YYYY-MM-DD' at noon UTC (TZ-safe). Returns null on malformed input. */
function parseDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / MS_DAY);
}

function fmtDate(d: Date, withYear: boolean): string {
  const base = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return withYear ? `${base}, ${d.getUTCFullYear()}` : base;
}

/** Season ordinal (UTC): `seasonMonths`-month blocks counted from Jan 1970. */
function seasonOf(d: Date, seasonMonths: number): number {
  return Math.floor((d.getUTCFullYear() * 12 + d.getUTCMonth()) / seasonMonths);
}

/** First day of the season AFTER the one containing `d` (= when points refresh). */
function seasonEndDate(d: Date, seasonMonths: number): Date {
  const next = (seasonOf(d, seasonMonths) + 1) * seasonMonths;
  return new Date(Date.UTC(Math.floor(next / 12), next % 12, 1, 12));
}

interface SeasonTally {
  steps: number;
  orderedDays: number;
  aims: number;
  goals: number;
  dayAwards: number;
  points: number;
  firstDate: Date | null;
}

/**
 * Compute all acquisition stats for one deity from its progress log + the
 * shared points ledger.
 *
 * SEASONS: points live inside a season (`game.seasonMonths`, default 3). Each
 * season is tallied on its own; when a season crosses a level threshold, that
 * level is BANKED forever. At season end, un-banked progress toward the next
 * level refreshes — the next season starts from the last banked floor.
 */
export function computeProgress(
  progress: DeityProgress | undefined,
  data: { levelIds: string[][]; perWeek: Record<string, number> },
  todayStr: string,
  _seed = '',
  ctx: ProgressContext = {},
): ProgressStats {
  const log = progress?.log ?? {};
  const picks = progress?.picks ?? [];
  const game = ctx.game ?? DEFAULT_GAME_SETTINGS;
  const ledger = ctx.ledger ?? {};
  const SM = game.seasonMonths;
  const today = parseDate(todayStr);
  const nowSeason = today ? seasonOf(today, SM) : 0;

  const logDates = Object.keys(log).filter((k) => Array.isArray(log[k]) && log[k].length > 0);
  const engaged = picks.length > 0 || logDates.length > 0;

  // Ordered weeks / months from the ledger (system-wide), each attributed to a season.
  const aimWeekSeasons = new Map<number, number[]>(); // season → week ordinals awarded AIM
  const goalWeekSeasons = new Map<number, number[]>(); // season → week ordinals closing a GOAL month
  const seasonOfWeek = (w: number) => {
    const startDay = w * 7 + ((game.weekBoundaryDay + 1) % 7) - 4;
    return seasonOf(new Date(startDay * MS_DAY + MS_DAY / 2), SM);
  };
  if (engaged) {
    const ow = orderedWeeks(ledger, game);
    for (const w of ow) {
      const s = seasonOfWeek(w);
      aimWeekSeasons.set(s, [...(aimWeekSeasons.get(s) ?? []), w]);
    }
    for (const w of goalAwardWeeks(ow, game)) {
      const s = seasonOfWeek(w);
      goalWeekSeasons.set(s, [...(goalWeekSeasons.get(s) ?? []), w]);
    }
  }

  // Group every dated entry (log + ledger) into seasons, ascending.
  const logBySeason = new Map<number, string[]>();
  const ledgerBySeason = new Map<number, string[]>();
  const seasonSet = new Set<number>();
  for (const k of logDates) {
    const d = parseDate(k);
    if (!d) continue;
    const s = seasonOf(d, SM);
    logBySeason.set(s, [...(logBySeason.get(s) ?? []), k]);
    seasonSet.add(s);
  }
  if (engaged) {
    for (const k of Object.keys(ledger)) {
      const d = parseDate(k);
      if (!d) continue;
      const s = seasonOf(d, SM);
      ledgerBySeason.set(s, [...(ledgerBySeason.get(s) ?? []), k]);
      seasonSet.add(s);
    }
    for (const s of [...aimWeekSeasons.keys(), ...goalWeekSeasons.keys()]) seasonSet.add(s);
  }
  const seasonKeys = Array.from(seasonSet).sort((a, b) => a - b);

  const tallySeason = (s: number): SeasonTally => {
    let steps = 0;
    let firstDate: Date | null = null;
    for (const k of logBySeason.get(s) ?? []) {
      steps += log[k].length;
      const d = parseDate(k)!;
      if (!firstDate || d < firstDate) firstDate = d;
    }
    let dayAwards = 0;
    let orderedDays = 0;
    for (const k of ledgerBySeason.get(s) ?? []) {
      const l = ledger[k];
      // Step actions are NOT re-counted from the ledger (the log is their source).
      const pts = dayPoints({ ...EMPTY_DAY, ...l, stepActions: 0 });
      dayAwards += pts;
      if (l.rowsGreen >= 5) orderedDays += 1;
      const d = parseDate(k)!;
      if (pts > 0 && (!firstDate || d < firstDate)) firstDate = d;
    }
    const aims = aimWeekSeasons.get(s)?.length ?? 0;
    const goals = goalWeekSeasons.get(s)?.length ?? 0;
    const points = steps * AWARD.STEP_ACTION + dayAwards + aims * AWARD.AIM + goals * AWARD.GOAL;
    return { steps, orderedDays, aims, goals, dayAwards, points, firstDate };
  };

  // Fold seasons: banked levels stick; in-progress points refresh each season.
  let bankedIdx = 0; // count of fully-banked LEVEL_THRESHOLDS
  let cur: SeasonTally | null = null;
  for (const s of seasonKeys) {
    if (s > nowSeason) break;
    const t = tallySeason(s);
    const floor = bankedIdx > 0 ? LEVEL_THRESHOLDS[bankedIdx - 1] : 0;
    const working = floor + t.points;
    while (bankedIdx < LEVEL_THRESHOLDS.length && working >= LEVEL_THRESHOLDS[bankedIdx]) bankedIdx += 1;
    if (s === nowSeason) cur = t;
  }

  // Current-season working numbers (a season with nothing yet = zero tally).
  const activeIds = data.levelIds[Math.min(bankedIdx, data.levelIds.length - 1)] ?? [];
  const activePicks = picks.filter((p) => activeIds.includes(p));
  const bankedFloor = bankedIdx > 0 ? LEVEL_THRESHOLDS[bankedIdx - 1] : 0;
  const points = bankedFloor + (cur?.points ?? 0);
  const maxed = bankedIdx >= LEVEL_THRESHOLDS.length;
  const level = maxed ? LEVEL_THRESHOLDS.length : bankedIdx + 1;
  const levelFloor = bankedFloor;
  const levelTarget = LEVEL_THRESHOLDS[Math.min(bankedIdx, LEVEL_THRESHOLDS.length - 1)];
  const secured = bankedIdx >= 1 || points >= ACQUIRE_POINTS;
  const pct = maxed ? 1 : Math.max(0, Math.min(1, (points - levelFloor) / (levelTarget - levelFloor)));

  const steps = cur?.steps ?? 0;
  const aims = cur?.aims ?? 0;
  const goals = cur?.goals ?? 0;
  const orderedDays = cur?.orderedDays ?? 0;
  const tierIdx = secured ? 4 : goals > 0 ? 3 : aims > 0 ? 2 : orderedDays > 0 || steps > 0 ? 1 : 0;

  // Rate from the current season only; the projection must land inside the
  // season (points refresh at its end), so cap the date at season end.
  let ratePerDay = 0;
  let projectedDate: string | null = null;
  let seasonEnd: string | null = null;
  if (today) {
    const endD = seasonEndDate(today, SM);
    seasonEnd = fmtDate(endD, endD.getUTCFullYear() !== today.getUTCFullYear());
    const first = cur?.firstDate ?? null;
    const elapsed = first ? Math.max(1, daysBetween(first, today) + 1) : 0;
    ratePerDay = elapsed > 0 ? (cur?.points ?? 0) / elapsed : 0;
    if (maxed) {
      projectedDate = 'maxed';
    } else if (ratePerDay > 0) {
      const daysLeft = Math.ceil((levelTarget - points) / ratePerDay);
      const target = new Date(today.getTime() + daysLeft * MS_DAY);
      projectedDate =
        target.getTime() > endD.getTime()
          ? `past season — resets ${seasonEnd}`
          : fmtDate(target, target.getUTCFullYear() !== today.getUTCFullYear());
    }
  }

  const doneToday = Array.isArray(log[todayStr]) ? log[todayStr] : [];

  return {
    points,
    steps,
    aims,
    goals,
    todayCount: orderedDays,
    dayAwards: cur?.dayAwards ?? 0,
    tierIdx,
    secured,
    level,
    levelTarget,
    levelFloor,
    maxed,
    pct,
    ratePerDay,
    projectedDate,
    seasonEnd,
    doneToday,
    activePicks,
  };
}

/** Picks are open any day (no weekday lock). The weekly pick is PROMPTED at the
 *  weekly boundary — the evening session that closes the week and opens the next
 *  (ruling 1.11). Pass `now` for testability. */
export function isPickPrompt(game: GameSettings = DEFAULT_GAME_SETTINGS, now: Date = new Date()): boolean {
  return isWeekBoundaryEvening(now, game);
}

/** Picks can always be edited. Kept as a named export for callers that gate on it. */
export function isPickDay(): boolean {
  return true;
}

/** Local 'YYYY-MM-DD' for today (device local time). */
export function todayKey(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
